import React, { useRef, useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import * as FiIcons from 'react-icons/fi';
import SafeIcon from '../common/SafeIcon';
import { COLORS } from '../utils/constants';

const { FiCamera, FiImage, FiX, FiZoomIn, FiZoomOut } = FiIcons;

const PhotoUpload = ({ photoPreview, onPhotoSelect }) => {
  const captureInputRef = useRef(null);
  const selectInputRef = useRef(null);
  const [zoom, setZoom] = useState(1);
  const [position, setPosition] = useState({ x: 0, y: 0 });
  const [isDragging, setIsDragging] = useState(false);
  const [dragStart, setDragStart] = useState({ x: 0, y: 0 });

  const handlePhotoSelect = (e) => {
    const file = e.target.files[0];
    if (file) {
      const reader = new FileReader();
      reader.onload = (event) => {
        onPhotoSelect(event.target.result);
        setZoom(1);
        setPosition({ x: 0, y: 0 });
      };
      reader.readAsDataURL(file);
    }
  };

  const removePhoto = () => {
    onPhotoSelect(null);
    setZoom(1);
    setPosition({ x: 0, y: 0 });
    if (captureInputRef.current) captureInputRef.current.value = '';
    if (selectInputRef.current) selectInputRef.current.value = '';
  };

  const handleZoomIn = () => setZoom(prev => Math.min(prev + 0.25, 3));
  const handleZoomOut = () => setZoom(prev => Math.max(prev - 0.25, 1));

  const handleMouseDown = (e) => {
    setIsDragging(true);
    setDragStart({ x: e.clientX - position.x, y: e.clientY - position.y });
  };

  const handleMouseMove = (e) => {
    if (isDragging) {
      setPosition({
        x: e.clientX - dragStart.x,
        y: e.clientY - dragStart.y
      });
    }
  };

  const handleMouseUp = () => {
    setIsDragging(false);
  };

  const handleTouchStart = (e) => {
    const touch = e.touches[0];
    setIsDragging(true);
    setDragStart({ x: touch.clientX - position.x, y: touch.clientY - position.y });
  };

  const handleTouchMove = (e) => {
    if (isDragging && e.touches.length === 1) {
      const touch = e.touches[0];
      setPosition({
        x: touch.clientX - dragStart.x,
        y: touch.clientY - dragStart.y
      });
    }
  };

  const handleTouchEnd = () => {
    setIsDragging(false);
  };

  return (
    <div className="mb-6">
      <AnimatePresence>
        {photoPreview && (
          <motion.div
            initial={{ opacity: 0, y: -20 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -20 }}
            className="mb-4"
          >
            <div 
              className="relative bg-gray-100 rounded-lg overflow-hidden"
              style={{ height: '300px' }}
            >
              <div
                className="absolute inset-0 cursor-move"
                onMouseDown={handleMouseDown}
                onMouseMove={handleMouseMove}
                onMouseUp={handleMouseUp}
                onMouseLeave={handleMouseUp}
                onTouchStart={handleTouchStart}
                onTouchMove={handleTouchMove}
                onTouchEnd={handleTouchEnd}
              >
                <img
                  src={photoPreview}
                  alt="Zähler Vorschau"
                  className="w-full h-full object-contain"
                  style={{
                    transform: `scale(${zoom}) translate(${position.x / zoom}px, ${position.y / zoom}px)`,
                    transition: isDragging ? 'none' : 'transform 0.2s'
                  }}
                  draggable={false}
                />
              </div>
              
              <div className="absolute top-2 right-2 flex gap-2">
                <button
                  onClick={handleZoomOut}
                  className="w-10 h-10 bg-white bg-opacity-90 rounded-full flex items-center justify-center hover:bg-opacity-100 transition-all shadow-lg"
                  disabled={zoom <= 1}
                >
                  <SafeIcon icon={FiZoomOut} size={20} />
                </button>
                <button
                  onClick={handleZoomIn}
                  className="w-10 h-10 bg-white bg-opacity-90 rounded-full flex items-center justify-center hover:bg-opacity-100 transition-all shadow-lg"
                  disabled={zoom >= 3}
                >
                  <SafeIcon icon={FiZoomIn} size={20} />
                </button>
                <button
                  onClick={removePhoto}
                  className="w-10 h-10 bg-red-500 text-white rounded-full flex items-center justify-center hover:bg-red-600 transition-colors shadow-lg"
                >
                  <SafeIcon icon={FiX} size={20} />
                </button>
              </div>

              <div className="absolute bottom-2 left-1/2 transform -translate-x-1/2 bg-black bg-opacity-70 text-white text-xs px-3 py-1 rounded-full">
                Zoom: {Math.round(zoom * 100)}% • Ziehen zum Verschieben
              </div>
            </div>
            
            <p className="text-sm text-gray-600 mt-2">
              📸 Verwenden Sie das Foto als Hilfe zum Abtippen des Zählerstands
            </p>
          </motion.div>
        )}
      </AnimatePresence>

      <div className="flex flex-wrap gap-3">
        <button
          onClick={() => captureInputRef.current?.click()}
          className="flex items-center gap-3 px-6 py-3 rounded-lg text-white font-medium transition-all hover:shadow-lg"
          style={{ backgroundColor: COLORS.secondary }}
        >
          <SafeIcon icon={FiCamera} />
          Foto aufnehmen
        </button>

        <button
          onClick={() => selectInputRef.current?.click()}
          className="flex items-center gap-3 px-6 py-3 rounded-lg border-2 font-medium transition-all hover:shadow-lg bg-white"
          style={{ borderColor: COLORS.secondary, color: COLORS.secondary }}
        >
          <SafeIcon icon={FiImage} />
          Foto auswählen
        </button>
      </div>

      <p className="text-xs text-gray-500 mt-2">
        📱 <strong>Android-Tipp:</strong> "Foto aufnehmen" öffnet die Kamera, "Foto auswählen" öffnet Ihre Galerie/Dateien
      </p>

      {/* Capture input - with capture attribute for camera */}
      <input
        ref={captureInputRef}
        type="file"
        accept="image/*"
        capture="environment"
        onChange={handlePhotoSelect}
        className="hidden"
      />

      {/* Select input - without capture for file picker */}
      <input
        ref={selectInputRef}
        type="file"
        accept="image/*"
        onChange={handlePhotoSelect}
        className="hidden"
      />
    </div>
  );
};

export default PhotoUpload;